import { useEffect } from 'react';
import {
  type AnalyticsUser,
  identify,
  incrementUserProperty,
  setUserProperties,
  setUserPropertiesOnce,
  timeEvent,
  track,
} from './analytics';
import { consumeSignIn } from './signInMarker';

/** Once per page load, not per mount: StrictMode mounts twice in development. */
let sessionTracked = false;

/**
 * Mount once inside the signed-in part of the app. Identifies the user (again
 * whenever their details change, e.g. their profile finishes loading or they
 * switch role) and records how this load began: a sign-in, which counts
 * towards `login_count`, or a stored session being picked up.
 *
 * Pass `null` until the user's id is known.
 */
export const useAnalyticsSession = (user: AnalyticsUser | null): void => {
  // Identity is compared by value, so callers can build the object inline.
  const identity = user ? JSON.stringify(user) : null;

  useEffect(() => {
    if (!identity) return;
    identify(JSON.parse(identity) as AnalyticsUser);
  }, [identity]);

  // Runs after the effect above, so the event lands on the identified user.
  const userId = user?.id;
  useEffect(() => {
    if (!userId || sessionTracked) return;
    sessionTracked = true;

    const method = consumeSignIn();
    if (!method) {
      track('Session Started');
      return;
    }

    const now = new Date().toISOString();
    track('User Signed In', { method });
    incrementUserProperty('login_count');
    setUserProperties({ last_login: now, last_login_method: method });
    setUserPropertiesOnce({ first_login: now });
    // `User Signed Out` then carries how long the session lasted.
    timeEvent('User Signed Out');
  }, [userId]);
};
